<template>
  <div class="followers-page">
    <div class="followers-shell">
      <div class="followers-header">
        <div>
          <div class="eyebrow">Curator audience</div>
          <h1>Followers</h1>
          <p>People who follow your curator channel and can receive collection update notifications.</p>
        </div>
        <div class="stat-card">
          <span class="stat-value">{{ followerCount }}</span>
          <span class="stat-label">Followers</span>
        </div>
      </div>

      <el-card class="followers-card">
        <div class="toolbar">
          <el-input
            v-model="searchText"
            placeholder="Search followers by name or email"
            clearable
            prefix-icon="el-icon-search"
            @keyup.enter.native="fetchFollowers(1)"
            @clear="fetchFollowers(1)"
          />
          <el-button type="primary" icon="el-icon-refresh" :loading="loading" @click="fetchFollowers(1)">
            Refresh
          </el-button>
        </div>

        <el-table
          v-loading="loading"
          :data="followers"
          empty-text="No followers yet"
          class="followers-table"
        >
          <el-table-column label="Follower" min-width="260">
            <template slot-scope="scope">
              <div class="follower-cell">
                <el-avatar
                  :size="42"
                  :src="scope.row.user_avatar_address_small"
                  class="avatar"
                >
                  {{ getInitial(scope.row.user_nickName || scope.row.user_name) }}
                </el-avatar>
                <div class="follower-main">
                  <router-link :to="{ path: '/profile', query: { id: scope.row.user_id } }" class="follower-name">
                    {{ scope.row.user_nickName || scope.row.user_name || 'Unnamed user' }}
                  </router-link>
                  <div class="follower-signature" v-if="scope.row.user_signature">
                    {{ scope.row.user_signature }}
                  </div>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="user_email" label="Email" min-width="220">
            <template slot-scope="scope">
              <span class="muted">{{ scope.row.user_email || 'Not available' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="country" label="Region" width="150">
            <template slot-scope="scope">
              <span class="muted">{{ scope.row.country || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Followed" width="160">
            <template slot-scope="scope">
              {{ formatDate(scope.row.followed_at) }}
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-row" v-if="resultCount > pageSize">
          <el-pagination
            background
            layout="prev, pager, next"
            :current-page.sync="page"
            :page-size="pageSize"
            :total="resultCount"
            @current-change="fetchFollowers"
          />
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import { postRequest } from "@/apis/front";

export default {
  name: "CuratorFollowers",
  data() {
    return {
      loading: false,
      followers: [],
      followerCount: 0,
      resultCount: 0,
      page: 1,
      pageSize: 20,
      searchText: "",
    };
  },
  created() {
    this.fetchFollowers(1);
  },
  methods: {
    async fetchFollowers(page) {
      this.page = page || this.page || 1;
      if (!this.$store.state.user_id) return;
      this.loading = true;
      try {
        const response = await postRequest("getCuratorFollowers", {
          user_id: this.$store.state.user_id,
          page: this.page,
          limit: this.pageSize,
          search_text: this.searchText,
        });
        if (response.data.code === 0) {
          const data = response.data.data || {};
          this.followers = data.followers || [];
          this.followerCount = data.follower_count || 0;
          this.resultCount = data.result_count || data.follower_count || 0;
        } else {
          this.$message.error(response.data.msg || "Failed to load followers");
        }
      } catch (error) {
        console.error("Failed to load followers:", error);
        this.$message.error("Failed to load followers");
      } finally {
        this.loading = false;
      }
    },
    getInitial(name) {
      return (name || "?").trim().charAt(0).toUpperCase() || "?";
    },
    formatDate(timestamp) {
      if (!timestamp) return "-";
      return new Date(timestamp * 1000).toLocaleDateString();
    },
  },
};
</script>

<style scoped>
.followers-page {
  min-height: calc(100vh - 60px);
  background: #f5f7fa;
  padding: 28px;
}

.followers-shell {
  max-width: 1120px;
  margin: 0 auto;
}

.followers-header {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: flex-end;
  margin-bottom: 20px;
}

.eyebrow {
  color: #409eff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.followers-header h1 {
  margin: 6px 0 8px;
  font-size: 28px;
  color: #303133;
}

.followers-header p {
  margin: 0;
  color: #606266;
  max-width: 640px;
  line-height: 1.6;
}

.stat-card {
  min-width: 150px;
  padding: 16px 20px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #e4e7ed;
  box-shadow: 0 8px 24px rgba(64, 158, 255, 0.08);
}

.stat-value {
  display: block;
  color: #303133;
  font-size: 30px;
  font-weight: 800;
  line-height: 1;
}

.stat-label {
  display: block;
  margin-top: 6px;
  color: #909399;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.followers-card {
  border-radius: 12px;
}

.toolbar {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.toolbar .el-input {
  max-width: 360px;
}

.follower-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar {
  flex-shrink: 0;
  background: #ecf5ff;
  color: #409eff;
}

.follower-main {
  min-width: 0;
}

.follower-name {
  color: #303133;
  font-weight: 700;
  text-decoration: none;
}

.follower-name:hover {
  color: #409eff;
}

.follower-signature {
  margin-top: 3px;
  color: #909399;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.muted {
  color: #909399;
}

.pagination-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

@media (max-width: 720px) {
  .followers-page {
    padding: 16px;
  }

  .followers-header,
  .toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .toolbar .el-input {
    max-width: none;
  }
}
</style>
